import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Save, X, Plus } from "lucide-react";
import ImageUpload from "./ImageUpload";
import VariantManager from "./VariantManager";
import ValidationErrors from "./ValidationErrors";
import FormField from "./FormField";
import { validateProductForm, formatValidationErrors } from "./FormValidation";
import { uploadRequest, API_BASE_URL } from "../../config/api";

const ProductForm = ({ product, categories, tags, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    brand: "",
    seo_title: "",
    seo_meta: "",
    seo_keywords: [],
    is_active: true,
    is_featured: false,
    categories: [],
    tags: [],
    variants: [
      {
        sku: "",
        price_regular: "",
        price_sale: "",
        stock: 0,
        weight_grams: "",
        is_default: true,
        is_active: true,
        attributes: [],
      },
    ],
  });

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState(null);
  const [keywordInput, setKeywordInput] = useState("");
  const [uploadFolder, setUploadFolder] = useState(
    new Date().getFullYear().toString()
  );
  const [uploadStatus, setUploadStatus] = useState(null); // 'success', 'error', 'uploading'
  const [statusMessage, setStatusMessage] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || "",
        description: product.description || "",
        brand: product.brand || "",
        seo_title: product.seo_title || "",
        seo_meta: product.seo_meta || "",
        seo_keywords: product.seo_keywords || [],
        is_active: product.is_active ?? true,
        is_featured: product.is_featured ?? false,
        categories:
          product.product_categories?.map((pc) => pc.category_id) || [],
        tags: product.product_tags?.map((pt) => pt.tag_id) || [],
        variants:
          product.product_variants?.map((variant) => ({
            id: variant.id,
            sku: variant.sku,
            price_regular: variant.price_regular,
            price_sale: variant.price_sale || "",
            stock: variant.stock,
            weight_grams:
              variant.weight_grams !== null &&
              variant.weight_grams !== undefined
                ? variant.weight_grams.toString()
                : "",
            is_default: variant.is_default,
            is_active: variant.is_active,
            attributes:
              variant.variant_attributes?.map((attr) => ({
                id: attr.id,
                name: attr.name,
                value: attr.value,
              })) || [],
            images:
              variant.images?.map((img) => ({
                id: img.id,
                url: img.url.startsWith("http")
                  ? img.url
                  : `${API_BASE_URL}${img.url.startsWith("/") ? "" : "/"}${img.url}`,
                alt_text: img.alt_text,
                is_primary: img.is_primary,
                position: img.position,
                existing: true,
              })) || [],
          })) || [],
      });

      // Set existing images and extract folder from image URLs
      if (product.images && product.images.length > 0) {
        setImages(
          product.images.map((img) => ({
            id: img.id,
            url: img.url.startsWith("http")
              ? img.url
              : `${API_BASE_URL}${img.url.startsWith("/") ? "" : "/"}${img.url}`,
            alt_text: img.alt_text,
            is_primary: img.is_primary,
            existing: true,
          }))
        );

        // Extract base folder from the first image URL
        // Main images: /uploads/2028/filename.webp -> 2028
        // Variant images: /uploads/2028/variant-1/filename.webp -> 2028
        const firstImageUrl = product.images[0].url;
        const folderMatch = firstImageUrl.match(/\/uploads\/([^/]+)/);
        if (folderMatch && folderMatch[1]) {
          // If it's a variant folder like "variant-1", we need to look at variant images
          // to get the base folder, otherwise use the matched folder
          let baseFolder = folderMatch[1];

          // Check if we have variant images to get the base folder from
          const hasVariantImages = product.product_variants?.some(
            (variant) => variant.images && variant.images.length > 0
          );

          if (hasVariantImages) {
            // Get folder from variant image URL: /uploads/2028/variant-1/file.webp -> 2028
            const variantImage = product.product_variants.find(
              (v) => v.images && v.images.length > 0
            )?.images[0];

            if (variantImage) {
              const variantFolderMatch = variantImage.url.match(
                /\/uploads\/([^/]+)\/variant-\d+\//
              );
              if (variantFolderMatch && variantFolderMatch[1]) {
                baseFolder = variantFolderMatch[1];
              }
            }
          }

          setUploadFolder(baseFolder);
          console.log(
            `📁 Extracted base folder from existing images: ${baseFolder}`
          );
        }
      }
    }
  }, [product]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addKeyword = () => {
    if (
      keywordInput.trim() &&
      !formData.seo_keywords.includes(keywordInput.trim())
    ) {
      handleArrayChange("seo_keywords", [
        ...formData.seo_keywords,
        keywordInput.trim(),
      ]);
      setKeywordInput("");
    }
  };

  const removeKeyword = (keyword) => {
    handleArrayChange(
      "seo_keywords",
      formData.seo_keywords.filter((k) => k !== keyword)
    );
  };

  const handleVariantsChange = (variants) => {
    setFormData((prev) => ({ ...prev, variants }));
  };

  const handleProductActiveChange = (isActive) => {
    // Update product active status and sync all variants
    setFormData((prev) => ({
      ...prev,
      is_active: isActive,
      variants: prev.variants.map((variant) => ({
        ...variant,
        is_active: isActive,
      })),
    }));
  };

  const handleImagesChange = (newImages) => {
    setImages(newImages);
  };

  const getFieldError = (fieldName) => {
    return validationErrors?.fieldErrors?.[fieldName] || null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setValidationErrors(null);
    setUploadStatus("uploading");
    setStatusMessage(product ? "Updating product..." : "Creating product...");
    setUploadProgress(0);

    try {
      // Client-side validation
      console.log("🔍 Running client-side validation...");
      const validation = validateProductForm(formData);

      if (!validation.isValid) {
        console.error("❌ Client-side validation failed:", validation.errors);
        setValidationErrors({
          fieldErrors: formatValidationErrors(validation.errors),
          debug: {
            source: "client-side",
            formData: formData,
          },
        });
        return;
      }

      console.log("✅ Client-side validation passed");

      // Check if at least one variant is default
      const hasDefaultVariant = formData.variants.some((v) => v.is_default);
      if (!hasDefaultVariant) {
        formData.variants[0].is_default = true;
      }

      // Prepare form data
      const submitData = new FormData();

      // Add basic product data
      Object.keys(formData).forEach((key) => {
        if (key === "variants") {
          // Clean variants by removing file objects before JSON.stringify
          const cleanVariants = formData.variants.map((variant) => {
            const cleanVariant = { ...variant };
            // Coerce stock and is_active so backend always receives correct types
            cleanVariant.stock =
              typeof variant.stock === "number"
                ? variant.stock
                : parseInt(variant.stock, 10) || 0;
            cleanVariant.is_active = variant.is_active !== false;
            if (cleanVariant.images) {
              cleanVariant.images = cleanVariant.images.map((img) => ({
                id: img.id,
                alt_text: img.alt_text,
                is_primary: img.is_primary,
                position: img.position,
                existing: img.existing,
                // Remove file and preview properties
              }));
            }
            return cleanVariant;
          });
          submitData.append(key, JSON.stringify(cleanVariants));
        } else if (
          key === "categories" ||
          key === "tags" ||
          key === "seo_keywords"
        ) {
          submitData.append(key, JSON.stringify(formData[key]));
        } else {
          submitData.append(key, formData[key]);
        }
      });

      // Add new product images
      const newImages = images.filter((img) => !img.existing && img.file);
      console.log("📸 Product images to upload:", newImages.length);
      const newImagesMetadata = [];
      newImages.forEach((img, index) => {
        submitData.append("images", img.file);
        // Add custom filename if provided
        if (img.custom_filename) {
          submitData.append(
            `image_${index}_custom_filename`,
            img.custom_filename
          );
        }
        // Collect metadata for new images
        newImagesMetadata.push({
          imageIndex: index,
          alt_text: img.alt_text,
          is_primary: img.is_primary,
        });
      });

      // Will combine with existing images metadata later

      // Add variant images
      const variantImages = [];
      formData.variants.forEach((variant, variantIndex) => {
        if (variant.images && variant.images.length > 0) {
          variant.images.forEach((image, imageIndex) => {
            if (image.file) {
              // This is a new image file
              console.log(
                `📸 Variant ${variantIndex} image ${imageIndex}:`,
                image.file.name
              );
              submitData.append(`variant_${variantIndex}_images`, image.file);

              // Add custom filename if provided
              if (image.custom_filename) {
                submitData.append(
                  `variant_${variantIndex}_image_${imageIndex}_custom_filename`,
                  image.custom_filename
                );
              }

              variantImages.push({
                variantIndex,
                imageIndex,
                alt_text: image.alt_text,
                is_primary: image.is_primary,
                position: image.position || imageIndex,
                custom_filename: image.custom_filename,
              });
            }
          });
        }
      });
      console.log("📸 Total variant images to upload:", variantImages.length);

      // Add variant images metadata
      if (variantImages.length > 0) {
        submitData.append(
          "variant_images_metadata",
          JSON.stringify(variantImages)
        );
      }

      // Combine all product images metadata (both new and existing)
      const allImagesMetadata = [];

      // Add metadata for new images
      newImagesMetadata.forEach((meta) => {
        allImagesMetadata.push(meta);
      });

      // Add metadata for existing images
      const existingImages = images.filter(
        (img) => img.existing && !img.remove
      );
      existingImages.forEach((img) => {
        allImagesMetadata.push({
          id: img.id,
          alt_text: img.alt_text,
          is_primary: img.is_primary,
          existing: true,
        });
      });

      // Add combined product images metadata
      if (allImagesMetadata.length > 0) {
        submitData.append("images_metadata", JSON.stringify(allImagesMetadata));
      }

      // Add folder from the upload folder state
      submitData.append(
        "folder",
        uploadFolder || new Date().getFullYear().toString()
      );

      // Add images to remove
      const imagesToRemove = images.filter((img) => img.existing && img.remove);
      if (imagesToRemove.length > 0) {
        submitData.append(
          "remove_images",
          JSON.stringify(imagesToRemove.map((img) => img.id))
        );
      }

      // Add variant images to remove
      const variantImagesToRemove = [];
      formData.variants.forEach((variant, variantIndex) => {
        if (variant.images) {
          variant.images.forEach((image, imageIndex) => {
            if (image.existing && image.remove) {
              variantImagesToRemove.push({
                variantIndex,
                imageId: image.id,
              });
            }
          });
        }
      });

      if (variantImagesToRemove.length > 0) {
        submitData.append(
          "remove_variant_images",
          JSON.stringify(variantImagesToRemove)
        );
      }

      // Always use uploadRequest for admin operations (it handles FormData properly)
      const url = product ? `/admin/products/${product.id}` : "/admin/products";
      const method = product ? "PUT" : "POST";

      await uploadRequest(
        url,
        submitData,
        (progress) => {
          console.log(`📊 Upload progress: ${progress}%`);
          setUploadProgress(progress);
          setStatusMessage(
            `${product ? "Updating" : "Creating"} product... ${progress}%`
          );
        },
        method
      );

      setUploadStatus("success");
      setStatusMessage(
        product
          ? "Product updated successfully!"
          : "Product created successfully!"
      );

      // Show success toast
      toast.success(
        product
          ? "Product updated successfully!"
          : "Product created successfully!"
      );

      // Call onSubmit callback to handle navigation (without API call)
      if (onSubmit) {
        onSubmit(); // Just for navigation, no data needed
      }

      setTimeout(() => {
        setUploadStatus(null);
        setStatusMessage("");
      }, 3000);
    } catch (err) {
      console.error("Product form submission error:", err);

      // Handle validation errors specifically
      if (err.response && err.response.status === 400) {
        const errorData = err.response.data;
        if (errorData.fieldErrors || errorData.errors) {
          setValidationErrors({
            errors: errorData.errors,
            fieldErrors: errorData.fieldErrors,
            debug: errorData.debug,
          });
        } else {
          setError(errorData.message || "Validation failed");
        }
      } else if (err.response && err.response.status === 409) {
        // Handle duplicate product
        const errorData = err.response.data;
        setError(
          <div>
            <p>{errorData.message}</p>
            {errorData.existing_product && (
              <button
                onClick={() =>
                  (window.location.href = `/admin/products/edit/${errorData.existing_product.id}`)
                }
                className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                Edit Existing Product
              </button>
            )}
          </div>
        );
      } else {
        setError(err.message || "Failed to save product");
      }
      setUploadStatus("error");
      setStatusMessage("Failed to save product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {product ? "Edit Product" : "Create New Product"}
            </h2>
            <p className="text-gray-600 mt-1">
              {product
                ? "Update product information and variants"
                : "Add a new product to your catalog"}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Validation Errors */}
          {validationErrors && (
            <ValidationErrors
              errors={validationErrors.errors}
              fieldErrors={validationErrors.fieldErrors}
              debug={validationErrors.debug}
            />
          )}

          {/* Upload Status */}
          {uploadStatus && (
            <div
              className={`px-4 py-3 rounded-lg border ${
                uploadStatus === "success"
                  ? "bg-green-50 border-green-200 text-green-700"
                  : uploadStatus === "error"
                  ? "bg-red-50 border-red-200 text-red-700"
                  : "bg-blue-50 border-blue-200 text-blue-700"
              }`}
            >
              <div className="flex items-center justify-between">
                <span>{statusMessage}</span>
                {uploadStatus === "uploading" && (
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* General Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
              Basic Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required={true}
                error={getFieldError("name")}
                placeholder="Enter product name"
              />

              <FormField
                label="Brand"
                name="brand"
                value={formData.brand}
                onChange={handleInputChange}
                error={getFieldError("brand")}
                placeholder="Enter brand name"
              />
            </div>

            <FormField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              error={getFieldError("description")}
              placeholder="Enter product description"
            >
              <textarea rows={4} placeholder="Enter product description" />
            </FormField>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      handleInputChange("is_active", e.target.checked)
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) =>
                      handleInputChange("is_featured", e.target.checked)
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Featured</span>
                </label>
              </div>
            </div>
          </div>

          {/* Categories and Tags */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
              Categories & Tags
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categories
                </label>
                <select
                  multiple
                  value={formData.categories}
                  onChange={(e) =>
                    handleArrayChange(
                      "categories",
                      Array.from(
                        e.target.selectedOptions,
                        (option) => option.value
                      )
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  size={5}
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  Hold Ctrl/Cmd to select multiple
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <select
                  multiple
                  value={formData.tags}
                  onChange={(e) =>
                    handleArrayChange(
                      "tags",
                      Array.from(
                        e.target.selectedOptions,
                        (option) => option.value
                      )
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  size={5}
                >
                  {tags.map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.name}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  Hold Ctrl/Cmd to select multiple
                </p>
              </div>
            </div>
          </div>

          {/* SEO */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
              SEO Settings
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SEO Title
                </label>
                <input
                  type="text"
                  value={formData.seo_title}
                  onChange={(e) =>
                    handleInputChange("seo_title", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SEO Keywords
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addKeyword())
                    }
                    placeholder="Enter keyword and press Enter"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={addKeyword}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus size={16} />
                  </button>
                </div>
                {formData.seo_keywords.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.seo_keywords.map((keyword, index) => (
                      <span
                        key={index}
                        className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm flex items-center gap-1"
                      >
                        {keyword}
                        <button
                          type="button"
                          onClick={() => removeKeyword(keyword)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SEO Meta Description
              </label>
              <textarea
                value={formData.seo_meta}
                onChange={(e) => handleInputChange("seo_meta", e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Images */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
              Product Images
            </h3>
            <ImageUpload
              images={images}
              onChange={handleImagesChange}
              maxImages={10}
              uploadFolder={uploadFolder}
              onFolderChange={setUploadFolder}
            />
          </div>

          {/* Variants */}
          <div className="space-y-6">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-lg font-medium text-gray-900">
                Product Variants
              </h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    handleProductActiveChange(e.target.checked)
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                />
                <span className="text-sm font-medium text-gray-700">
                  Product Active (affects all variants)
                </span>
              </label>
            </div>
            <VariantManager
              variants={formData.variants}
              onChange={handleVariantsChange}
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  {product ? "Update Product" : "Create Product"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
